<template>
  <NuxtLayout :name="layoutName" :navbar-color="layoutNavbarColor">
    <div v-if="story">
      <section class="container author-page__container">
        <BlogBreadcrumbs class="author-page__breadcrumbs" />
        <div class="author-page">
          <BlogAuthorCard
            class="author-page__card"
            :author="story"
            :is-large="true"
            :disable-link="true"
          />
          <section class="author-page__grid">
            <BlogArticlePreview
              v-for="article in loadedElements"
              :key="article.uuid"
              :article="article"
            />
          </section>
        </div>
        <div
          v-if="!hideLoadButton"
          ref="loaderEl"
          class="author-page__load-section"
        >
          <LoaderSpinner v-if="isLoading && !isError" blue />
          <p v-if="isError" class="author-page__error">
            {{ $t('common.something_went_wrong') }}
          </p>
        </div>
      </section>
      <section class="author-page__newsletter-section">
        <div class="container">
          <BlogNewsletterCard />
        </div>
      </section>
    </div>
  </NuxtLayout>
</template>

<script lang="ts" setup>
import type { BlogAuthorStoryblok } from '@/types'
const loaderEl = ref(null)

const { slug } = useRoute().params

const { story } = await useFetchStory<BlogAuthorStoryblok>(
  `blog/authors/${slug}`,
)

const queryParams = {
  starts_with: 'blog/articles',
  resolve_relations: ['sb-blogpost.authors, sb-blogpost.categories'],
  sort_by: 'first_published_at:desc',
  per_page: 12,
  filter_query: {
    authors: {
      in_array: story.value.uuid,
    },
  },
}

const {
  initObserver,
  isLoading,
  hideLoadButton,
  loadedElements,
  loadFirstPage,
  isError,
} = useLoadMore(queryParams)

loadFirstPage()

if (story.value.content.component !== 'blog-author') {
  throw new Error('Unexpected story content type')
}

const metaTitle = `Author - ${story.value.name} - Infermedica`

const { getBreadcrumbsJsonLd } = useBreadcrumbs()

useCustomHead(story, {
  title: metaTitle,
  metaArgs: [
    {
      hid: 'title',
      name: 'title',
      content: metaTitle,
    },
    {
      hid: 'og:title',
      name: 'og:title',
      content: metaTitle,
    },
    {
      hid: 'twitter:title',
      name: 'twitter:title',
      content: metaTitle,
    },
  ],
  jsonLd: getBreadcrumbsJsonLd(),
})
false && /*#__PURE__*/ defineI18nRoute({
  locales: ['en'],
})
const { layoutName, layoutNavbarColor } = useLayout<any>(story)

onMounted(() => {
  initObserver(loaderEl.value)
})

watch(loaderEl, () => {
  initObserver(loaderEl.value)
})
</script>

<style lang="scss" scoped>
.author-page {
  &__container {
    margin-bottom: 120px;
  }

  &__card {
    display: block;
    max-width: 660px;
  }

  &__grid {
    padding-top: 56px;
    display: grid;
    grid-template-columns: 1fr;
    row-gap: 48px;
    margin-bottom: 56px;

    @media (min-width: $breakpoint-lg) {
      padding-top: 96px;
      row-gap: 56px;
      column-gap: 56px;
      grid-template-columns: 1fr 1fr;
      margin-bottom: 96px;
    }
  }

  &__load-section {
    min-height: 20px;
    width: 100%;
    margin: 40px auto;
    margin: 20px auto 0 auto;
    text-align: center;
  }

  &__newsletter-section {
    background-color: var(--paper-100);
    padding: 80px 0;

    @media (min-width: $breakpoint-md) {
      padding: 128px 0;
    }
  }

  &__error {
    padding-top: 40px;
    display: block;
    font-weight: var(--font-weight-paragraph-bold);
  }
}
</style>
